import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Paper,
  Container,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { GroupAccordion } from './GroupAccordion';
import { CreateGroupModal, ChannelSelectionModal } from './ChannelSelectionModal';
import { GroupAssignmentModal } from './GroupAssignmentModal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { EditGroupNameModal } from './EditGroupNameModal';
import { AUTH_ENDPOINTS, USER_ENDPOINTS } from '../../config/api';
import type { UserData, GroupData } from '../../types/api.types';
import { useUserData, userDataQueryKey } from '../../hooks/useUserData';
import { useUserSubscriptions } from '../../hooks/useUserSubscriptions';
import { extractApiErrorMessage } from '../../utils/api';
import {
  FREE_GROUP_CHANNEL_LIMIT,
  FREE_GROUP_LIMIT,
  PRO_GROUP_CHANNEL_LIMIT,
  getChannelLimit,
} from '../../utils/limits';

export const Dashboard = () => {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const { data: userData, error: userDataError, isLoading: isUserDataLoading } = useUserData();
  const {
    subscriptions,
    error: subscriptionsError,
    isLoading: isSubscriptionsLoading,
    forceRefreshSubscriptions,
  } = useUserSubscriptions();
  const [groupData, setGroupData] = useState<GroupData[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState<boolean>(false);
  const [addChannelsTarget, setAddChannelsTarget] = useState<string | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const [assignGroupsChannelId, setAssignGroupsChannelId] = useState<string | null>(null);
  // Bulk selection for Ungrouped
  const [ungroupedSelectMode, setUngroupedSelectMode] = useState<boolean>(false);
  const [selectedUngroupedIds, setSelectedUngroupedIds] = useState<string[]>([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState<boolean>(false);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    description?: string;
    confirmText?: string;
    context?: { type: 'remove-channel' | 'delete-group'; group: GroupData; channelId?: string };
  }>({ open: false, title: '' });

  // Edit group name modal target reference
  const [editGroupTarget, setEditGroupTarget] = useState<GroupData | null>(null);

  const loading = !isLoaded || (isSignedIn && (isUserDataLoading || isSubscriptionsLoading));
  type CodedError = Error & { code?: string };
  const subscriptionsErrorCode = (subscriptionsError as CodedError | undefined)?.code;
  const needsReauthorization =
    (!!userData?.profile.email && userData?.isGoogleConnected === false) ||
    subscriptionsErrorCode === 'reauthorize_required' ||
    (subscriptionsError?.message?.toLowerCase().includes('reauthorize') ?? false);
  const reauthorizationMessage =
    subscriptionsError?.message ?? 'Your Google authorization expired. Please reconnect your YouTube account.';
  const blockingError =
    userDataError?.message ?? (!needsReauthorization ? (subscriptionsError?.message ?? null) : null);

  const updateGroups = (groups: GroupData[]) => {
    setGroupData(groups);
    queryClient.setQueryData(userDataQueryKey, (previous: UserData | null) => {
      if (!previous) {
        return previous;
      }
      return { ...previous, groups };
    });
  };

  useEffect(() => {
    if (userData?.groups) {
      setGroupData(userData.groups);
    } else if (userData === null) {
      setGroupData([]);
    }
  }, [userData]);

  // Calculate ungrouped subscriptions
  const ungroupedSubscriptions = useMemo(() => {
    const channelIdsInGroups = new Set(groupData?.flatMap(group => group.channelIds) ?? []);

    return subscriptions.filter(subscription => !channelIdsInGroups.has(subscription.channelId));
  }, [subscriptions, groupData]);

  // Bulk selection helpers
  const allUngroupedIds = useMemo(() => ungroupedSubscriptions.map(s => s.channelId), [ungroupedSubscriptions]);
  const allSelected = useMemo(
    () => selectedUngroupedIds.length > 0 && selectedUngroupedIds.length === allUngroupedIds.length,
    [selectedUngroupedIds, allUngroupedIds],
  );

  if (loading) {
    return (
      <Container sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!isSignedIn) {
    return (
      <Container sx={{ py: 3 }}>
        <Alert severity="error">Please sign in to access your groups.</Alert>
      </Container>
    );
  }

  if (blockingError) {
    return (
      <Container sx={{ py: 3 }}>
        <Alert severity="error">{blockingError}</Alert>
        {blockingError.toLowerCase().includes('reauthorize') && (
          <Box mt={2}>
            <Button variant="contained" color="primary" href={AUTH_ENDPOINTS.GOOGLE_LOGIN}>
              Connect YouTube
            </Button>
          </Box>
        )}
      </Container>
    );
  }

  // Handle accordion expansion
  const handleAccordionChange = (panel: string) => () => {
    setExpandedAccordion(expandedAccordion === panel ? false : panel);
  };

  const enterBulkMode = () => {
    setUngroupedSelectMode(true);
    setSelectedUngroupedIds([]);
  };
  const cancelBulkMode = () => {
    setUngroupedSelectMode(false);
    setSelectedUngroupedIds([]);
  };
  const toggleSelect = (id: string) => {
    setSelectedUngroupedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUngroupedIds([]);
    } else {
      setSelectedUngroupedIds(allUngroupedIds);
    }
  };

  // Handle group creation
  const handleCreateGroup = async (name: string, channelIds: string[]) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not available');

      // Create the new group
      const newGroup: GroupData = { name, channelIds };

      // Prepare the updated groups array
      const updatedGroups = [...groupData, newGroup];

      // PATCH the user endpoint with updated groups
      const response = await fetch(`${USER_ENDPOINTS.USER}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groups: updatedGroups }),
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to create group'));
      }

      // parse response body
      const data = await response.json();
      const newGroupData = data.groups;
      // Update the local state with the new group
      updateGroups(newGroupData);

      // Show success message
      setSnackbarMessage(`Group "${name}" created successfully!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to create group');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw err;
    }
  };

  // Handle adding channels to existing group
  const handleAddChannels = async (groupName: string, channelIds: string[]) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not available');

      // Find the existing group and add the new channels
      const updatedGroups =
        groupData?.map(group => {
          if (group.name === groupName) {
            // Create a Set to ensure unique channel IDs
            const uniqueChannelIds = Array.from(new Set([...group.channelIds, ...channelIds]));
            return { ...group, channelIds: uniqueChannelIds };
          }
          return group;
        }) || [];

      // PATCH the user endpoint with updated groups
      const response = await fetch(`${USER_ENDPOINTS.USER}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groups: updatedGroups }),
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to add channels to group'));
      }

      // parse response body
      const data = await response.json();
      const newGroupData = data.groups;
      // Update local state
      updateGroups(newGroupData);

      // Show success message
      const channelCount = channelIds.length;
      setSnackbarMessage(`Added ${channelCount} channel${channelCount !== 1 ? 's' : ''} to "${groupName}"`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to add channels');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw err;
    }
  };

  // Handle removing channel from group
  const handleRemoveFromGroup = async (groupName: string, channelId: string) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not available');

      // Prepare the updated groups array with the channel removed
      const updatedGroups =
        groupData?.map(group =>
          group.name === groupName ? { ...group, channelIds: group.channelIds.filter(id => id !== channelId) } : group,
        ) || [];

      // PATCH the user endpoint with updated groups
      const response = await fetch(`${USER_ENDPOINTS.USER}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groups: updatedGroups }),
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to remove channel from group'));
      }

      // parse response body
      const data = await response.json();
      const newGroupData = data.groups;
      // Update local state
      updateGroups(newGroupData);

      // Show success message
      const subscription = subscriptions.find(sub => sub.channelId === channelId);
      setSnackbarMessage(`Removed "${subscription?.channel.title}" from "${groupName}"`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to remove channel');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Open confirmation to remove a channel from a group
  const confirmRemoveFromGroup = (group: GroupData, channelId: string) => {
    const subscription = subscriptions.find(sub => sub.channelId === channelId);
    const channelTitle = subscription?.channel.title ?? 'this channel';
    setConfirmState({
      open: true,
      title: 'Remove from group?',
      description: `Remove "${channelTitle}" from "${group.name}"?`,
      confirmText: 'Remove',
      context: { type: 'remove-channel', group, channelId },
    });
  };

  // Placeholder to confirm deleting a group (action not implemented yet)
  const confirmDeleteGroup = (group: GroupData) => {
    setConfirmState({
      open: true,
      title: 'Delete group?',
      description: `Delete the group "${group.name}" and remove all its channel assignments? This cannot be undone.`,
      confirmText: 'Delete Group',
      context: { type: 'delete-group', group },
    });
  };

  const handleDeleteGroup = async (group: GroupData) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not available');

      const response = await fetch(`${USER_ENDPOINTS.USER}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groups: groupData?.filter(g => g.id !== group.id) }),
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to delete group'));
      }

      // parse response body
      const data = await response.json();
      const newGroupData = data.groups;
      // Update local state
      updateGroups(newGroupData);

      // Show success message
      setSnackbarMessage(`Deleted group "${group.name}"`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to delete group');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle renaming a group (validation + backend + state)
  const handleRenameGroup = async (group: GroupData, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) {
      throw new Error('Please enter a group name');
    }
    // Ensure uniqueness
    const nameExists = (groupData || []).some(
      g => (group.id ? g.id !== group.id : g.name !== group.name) && g.name === trimmed,
    );
    if (nameExists) {
      throw new Error('A group with this name already exists');
    }

    const token = await getToken();
    if (!token) throw new Error('Authentication token not available');

    const updatedGroups =
      groupData?.map(g => {
        const isTarget = group.id ? g.id === group.id : g.name === group.name;
        return isTarget ? { ...g, name: trimmed } : g;
      }) || [];

    const response = await fetch(`${USER_ENDPOINTS.USER}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ groups: updatedGroups }),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response, 'Failed to rename group'));
    }

    const data = await response.json();
    const newGroupData = data.groups;
    updateGroups(newGroupData);

    setSnackbarMessage(`Renamed group "${group.name}" to "${trimmed}"`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Get subscriptions for a specific group
  const getGroupSubscriptions = (channelIds: string[]) => {
    return subscriptions.filter(sub => channelIds.includes(sub.channelId));
  };

  // Get available channels for adding to a group (not already in the group)
  const getAvailableChannelsForGroup = (groupName: string) => {
    const group = groupData?.find(g => g.name === groupName);
    if (!group) return ungroupedSubscriptions;
    return ungroupedSubscriptions.filter(sub => !group.channelIds.includes(sub.channelId));
  };

  // Handle assigning an ungrouped channel to groups (and optionally a new group)
  const handleAssignChannelToGroups = async (
    channelId: string,
    opts: { selectedGroupNames: string[]; newGroupName?: string },
  ) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not available');

      const { selectedGroupNames, newGroupName } = opts;
      const channelLimit = getChannelLimit(!!userData?.pro);

      const nameToGroup = new Map(groupData.map(g => [g.name, { ...g }]));

      // Add channel to selected groups
      selectedGroupNames.forEach(name => {
        const g = nameToGroup.get(name);
        if (g) {
          if (!g.channelIds.includes(channelId) && g.channelIds.length >= channelLimit) {
            throw new Error(
              userData?.pro
                ? `Groups can include up to ${PRO_GROUP_CHANNEL_LIMIT} channels.`
                : `Free plan allows up to ${FREE_GROUP_CHANNEL_LIMIT} channels per group. Upgrade to save up to ${PRO_GROUP_CHANNEL_LIMIT} channels per group.`,
            );
          }
          g.channelIds = Array.from(new Set([...g.channelIds, channelId]));
          nameToGroup.set(name, g);
        }
      });

      // Optionally create a new group and add the channel
      if (newGroupName && newGroupName.trim()) {
        const trimmed = newGroupName.trim();
        if (nameToGroup.has(trimmed)) {
          throw new Error('A group with this name already exists');
        }
        if (!userData?.pro && nameToGroup.size >= FREE_GROUP_LIMIT) {
          throw new Error(`Free plan allows up to ${FREE_GROUP_LIMIT} groups. Upgrade for unlimited groups.`);
        }
        nameToGroup.set(trimmed, { name: trimmed, channelIds: [channelId] });
      }

      const updatedGroups = Array.from(nameToGroup.values());

      const response = await fetch(`${USER_ENDPOINTS.USER}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groups: updatedGroups }),
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to update groups'));
      }

      // parse response body
      const data = await response.json();
      const newGroupData = data.groups;
      // Update the local state with the new group
      updateGroups(newGroupData);

      setSnackbarMessage('Channel added to group(s)');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setAssignGroupsChannelId(null);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to add to groups');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw err;
    }
  };

  // Handle assigning multiple ungrouped channels to groups
  const handleAssignChannelsToGroups = async (
    channelIds: string[],
    opts: { selectedGroupNames: string[]; newGroupName?: string },
  ) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not available');

      const { selectedGroupNames, newGroupName } = opts;
      const channelLimit = getChannelLimit(!!userData?.pro);

      const nameToGroup = new Map(groupData.map(g => [g.name, { ...g }]));

      // Add channels to selected groups
      selectedGroupNames.forEach(name => {
        const g = nameToGroup.get(name);
        if (g) {
          const nextSize = new Set([...g.channelIds, ...channelIds]).size;
          if (nextSize > channelLimit) {
            throw new Error(
              userData?.pro
                ? `Groups can include up to ${PRO_GROUP_CHANNEL_LIMIT} channels.`
                : `Free plan allows up to ${FREE_GROUP_CHANNEL_LIMIT} channels per group. Upgrade to save up to ${PRO_GROUP_CHANNEL_LIMIT} channels per group.`,
            );
          }
          g.channelIds = Array.from(new Set([...g.channelIds, ...channelIds]));
          nameToGroup.set(name, g);
        }
      });

      // Optionally create a new group and add the channels
      if (newGroupName && newGroupName.trim()) {
        const trimmed = newGroupName.trim();
        if (nameToGroup.has(trimmed)) {
          throw new Error('A group with this name already exists');
        }
        if (!userData?.pro && nameToGroup.size >= FREE_GROUP_LIMIT) {
          throw new Error(`Free plan allows up to ${FREE_GROUP_LIMIT} groups. Upgrade for unlimited groups.`);
        }
        if (channelIds.length > channelLimit) {
          throw new Error(
            userData?.pro
              ? `Groups can include up to ${PRO_GROUP_CHANNEL_LIMIT} channels.`
              : `Free plan allows up to ${FREE_GROUP_CHANNEL_LIMIT} channels per group. Upgrade to save up to ${PRO_GROUP_CHANNEL_LIMIT} channels per group.`,
          );
        }
        nameToGroup.set(trimmed, { name: trimmed, channelIds: [...channelIds] });
      }

      const updatedGroups = Array.from(nameToGroup.values());

      const response = await fetch(`${USER_ENDPOINTS.USER}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ groups: updatedGroups }),
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to update groups'));
      }

      // parse response body
      const data = await response.json();
      const newGroupData = data.groups;
      // Update the local state with the new group
      updateGroups(newGroupData);

      setSnackbarMessage(
        `Added ${channelIds.length} channel${channelIds.length !== 1 ? 's' : ''} to selected group(s)`,
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Exit bulk mode and clear selection
      setBulkAssignOpen(false);
      cancelBulkMode();
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to add to groups');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw err;
    }
  };

  const refreshSubscriptions = async () => {
    try {
      await forceRefreshSubscriptions();
      setSnackbarMessage('Subscriptions refreshed from YouTube.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err instanceof Error ? err.message : 'Failed to refresh subscriptions.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <Container sx={{ py: 3 }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Create Group Modal */}
      <CreateGroupModal
        open={createGroupModalOpen}
        onClose={() => setCreateGroupModalOpen(false)}
        subscriptions={ungroupedSubscriptions}
        onCreateGroup={handleCreateGroup}
        existingGroupNames={groupData?.map(g => g.name) || []}
        userHasPro={!!userData?.pro}
        userBillingId={userData?.billingId || ''}
      />

      {/* Assign Ungrouped Channel to Groups Modal */}
      {assignGroupsChannelId && (
        <GroupAssignmentModal
          open={!!assignGroupsChannelId}
          onClose={() => setAssignGroupsChannelId(null)}
          groups={groupData || []}
          // channelId={assignGroupsChannelId}
          onAssign={opts => handleAssignChannelToGroups(assignGroupsChannelId, opts)}
          userHasPro={!!userData?.pro}
          userBillingId={userData?.billingId || ''}
        />
      )}

      {/* Bulk Assign Multiple Ungrouped Channels Modal */}
      {bulkAssignOpen && (
        <GroupAssignmentModal
          open={bulkAssignOpen}
          onClose={() => setBulkAssignOpen(false)}
          groups={groupData || []}
          onAssign={opts => handleAssignChannelsToGroups(selectedUngroupedIds, opts)}
          userHasPro={!!userData?.pro}
          userBillingId={userData?.billingId || ''}
        />
      )}

      {/* Add Channels to Group Modal */}
      {addChannelsTarget && (
        <ChannelSelectionModal
          open={!!addChannelsTarget}
          onClose={() => setAddChannelsTarget(null)}
          mode="add"
          groupName={addChannelsTarget}
          subscriptions={getAvailableChannelsForGroup(addChannelsTarget)}
          onAddChannels={handleAddChannels}
          existingChannelIds={groupData?.find(g => g.name === addChannelsTarget)?.channelIds || []}
          userHasPro={!!userData?.pro}
          userBillingId={userData?.billingId || ''}
        />
      )}

      {/* Edit Group Name Modal */}
      {editGroupTarget && (
        <EditGroupNameModal
          open={!!editGroupTarget}
          group={editGroupTarget}
          onClose={() => setEditGroupTarget(null)}
          onRename={handleRenameGroup}
        />
      )}

      {/* Confirmations */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onClose={() => setConfirmState({ open: false, title: '' })}
        onConfirm={async () => {
          const ctx = confirmState.context;
          if (!ctx) return;
          if (ctx.type === 'remove-channel' && ctx.channelId) {
            await handleRemoveFromGroup(ctx.group.name, ctx.channelId);
          }
          if (ctx.type === 'delete-group' && ctx.group) {
            await handleDeleteGroup(ctx.group);
          }
          setConfirmState({ open: false, title: '' });
        }}
      />

      {needsReauthorization && (
        <Alert
          severity="warning"
          variant="outlined"
          action={
            <Button color="warning" variant="contained" href={AUTH_ENDPOINTS.GOOGLE_LOGIN}>
              Connect Google Account
            </Button>
          }
        >
          {reauthorizationMessage}
        </Alert>
      )}

      {/* Check if user needs to connect Google/YouTube account */}
      {userData && (!userData.profile?.email || subscriptions.length === 0) ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Connect Your YouTube Account
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              To manage your YouTube subscriptions and create channel groups, you need to connect your Google account.
            </Typography>
            <Button variant="contained" color="primary" href={AUTH_ENDPOINTS.GOOGLE_LOGIN} size="large" sx={{ mt: 1 }}>
              Connect Google Account
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* Show content if user has YouTube connected */}
      {userData && userData.profile?.email && subscriptions.length > 0 ? (
        <>
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" component="h2">
                Manage Groups
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {/* <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={refreshSubscriptions}
                  disabled={ungroupedSubscriptions.length === 0}
                  data-cy="refresh-subscriptions-btn"
                >
                  Refresh Subscriptions
                </Button> */}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateGroupModalOpen(true)}
                  disabled={ungroupedSubscriptions.length === 0}
                  data-cy="create-group-btn"
                >
                  Create Group
                </Button>
              </Box>
            </Box>

            {/* Show message if no groups exist */}
            {(!groupData || groupData.length === 0) && (
              <Paper sx={{ p: 4, mb: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  You haven't created any groups yet
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Organize your {subscriptions.length} YouTube subscriptions into groups for better management.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateGroupModalOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Create Your First Group
                </Button>
              </Paper>
            )}

            {/* Show groups as accordions */}
            <Box sx={{ mb: 3 }}>
              {groupData?.map(group => (
                <GroupAccordion
                  key={group.name}
                  groupName={group.name}
                  groupId={group.id}
                  subscriptions={getGroupSubscriptions(group.channelIds)}
                  onRemoveFromGroup={channelId => confirmRemoveFromGroup(group, channelId)}
                  onAddChannels={() => setAddChannelsTarget(group.name)}
                  expanded={expandedAccordion === group.name}
                  onChange={handleAccordionChange(group.name)}
                  onDeleteGroup={() => confirmDeleteGroup(group)}
                  onEditGroupName={() => setEditGroupTarget(group)}
                />
              ))}

              {/* Ungrouped channels accordion */}
              {ungroupedSubscriptions.length > 0 && (
                <GroupAccordion
                  groupName="Ungrouped Channels"
                  subscriptions={ungroupedSubscriptions}
                  isUngrouped={true}
                  onAddToGroups={channelId => setAssignGroupsChannelId(channelId)}
                  expanded={expandedAccordion === 'ungrouped'}
                  onChange={handleAccordionChange('ungrouped')}
                  // Bulk selection props
                  isBulkMode={ungroupedSelectMode}
                  selectedIds={selectedUngroupedIds}
                  onStartBulkSelect={enterBulkMode}
                  onCancelBulkSelect={cancelBulkMode}
                  onToggleSelect={toggleSelect}
                  onToggleSelectAll={toggleSelectAll}
                  allSelected={allSelected}
                  onConfirmBulkAssign={() => setBulkAssignOpen(true)}
                  refreshSubscriptions={refreshSubscriptions}
                />
              )}
            </Box>

            {/* Summary info */}
            <Alert severity="info">
              Total: {subscriptions.length} subscriptions • {groupData?.length || 0} groups •
              {ungroupedSubscriptions.length} ungrouped
            </Alert>
          </Box>
        </>
      ) : (
        // Show a message if user data is still loading or not connected
        !userData && !loading && <Alert severity="info">Please sign in to continue.</Alert>
      )}
    </Container>
  );
};
